// Medical Scores Database - Complete Collection Part 13
// Additional scores from medicalcul.free.fr - G-L section

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  User,
  Beaker,
  Target
} from 'lucide-react';

export const SCORES_PART_13 = {
  // ==================== SCORES G ====================
  gds15: {
    name: 'GDS-15 (Dépression gériatrique)',
    category: 'Gériatrie',
    icon: User,
    description: 'Dépistage dépression du sujet âgé',
    color: 'violet',
    fields: [
      { id: 'satisfied', label: 'Êtes-vous satisfait de votre vie?', type: 'select', options: [{ value: 0, label: 'Oui' }, { value: 1, label: 'Non' }]},
      { id: 'dropped', label: 'Avez-vous abandonné beaucoup d\'activités?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'empty', label: 'Sentez-vous que votre vie est vide?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'bored', label: 'Vous ennuyez-vous souvent?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'spirits', label: 'Êtes-vous de bonne humeur la plupart du temps?', type: 'select', options: [{ value: 0, label: 'Oui' }, { value: 1, label: 'Non' }]},
      { id: 'afraid', label: 'Craignez-vous qu\'il vous arrive quelque chose de mal?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'happy', label: 'Vous sentez-vous heureux la plupart du temps?', type: 'select', options: [{ value: 0, label: 'Oui' }, { value: 1, label: 'Non' }]},
      { id: 'helpless', label: 'Avez-vous l\'impression d\'être désarmé?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'home', label: 'Préférez-vous rester chez vous plutôt que sortir?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'memory', label: 'Pensez-vous avoir plus de problèmes de mémoire?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'alive', label: 'Pensez-vous que c\'est merveilleux d\'être en vie?', type: 'select', options: [{ value: 0, label: 'Oui' }, { value: 1, label: 'Non' }]},
      { id: 'worthless', label: 'Vous sentez-vous inutile?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'energy', label: 'Vous sentez-vous plein d\'énergie?', type: 'select', options: [{ value: 0, label: 'Oui' }, { value: 1, label: 'Non' }]},
      { id: 'hopeless', label: 'Avez-vous l\'impression que votre situation est sans espoir?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'betterOff', label: 'Pensez-vous que les autres sont mieux lotis?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]}
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += v; });
      return { score: total, max: 15 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `GDS-15 ${score}/15`, detail: 'Pas de dépression. Surveillance standard.' };
      if (score <= 8) return { level: 'warning', text: `GDS-15 ${score}/15`, detail: 'Dépression légère possible. Évaluation psychiatrique recommandée.' };
      if (score <= 11) return { level: 'danger', text: `GDS-15 ${score}/15`, detail: 'Dépression modérée. Traitement antidépresseur à discuter.' };
      return { level: 'danger', text: `GDS-15 ${score}/15`, detail: 'Dépression sévère. Traitement et suivi spécialisé urgent.' };
    },
    formula: '0-4: normal. 5-8: léger. 9-11: modéré. 12-15: sévère'
  },

  gemsa: {
    name: 'GEMSA (Consommation ressources urgences)',
    category: 'Urgences',
    icon: Stethoscope,
    description: 'Classification consommation ressources aux urgences',
    color: 'blue',
    fields: [
      { id: 'class', label: 'Classification GEMSA', type: 'select', options: [
        { value: 1, label: 'Groupe 1 - Décès à l\'arrivée ou peu après' },
        { value: 2, label: 'Groupe 2 - Instable, réanimation' },
        { value: 3, label: 'Groupe 3 - Diagnostiqué, stabilisé, hospitalisé' },
        { value: 4, label: 'Groupe 4 - Diagnostiqué, stabilisé, retour domicile' },
        { value: 5, label: 'Groupe 5 - Examen complémentaire, pas de diagnostic' },
        { value: 6, label: 'Groupe 6 - Sorti sans examen complémentaire' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.class || 6, max: 'Classification' };
    },
    interpret: (score) => {
      const interpretations = {
        1: { level: 'danger', text: 'GEMSA 1', detail: 'Patient décédé. Forte consommation de ressources.' },
        2: { level: 'danger', text: 'GEMSA 2', detail: 'Patient instable nécessitant réanimation. Très forte consommation.' },
        3: { level: 'warning', text: 'GEMSA 3', detail: 'Diagnostic posé, patient hospitalisé. Consommation importante.' },
        4: { level: 'success', text: 'GEMSA 4', detail: 'Diagnostic posé, retour domicile. Consommation modérée.' },
        5: { level: 'warning', text: 'GEMSA 5', detail: 'Examens réalisés mais diagnostic incertain. Bilan non rentable.' },
        6: { level: 'success', text: 'GEMSA 6', detail: 'Sortie sans examen. Faible consommation de ressources.' }
      };
      return interpretations[score] || interpretations[6];
    },
    formula: 'Classification française des urgences pour analyse activité'
  },

  girerd: {
    name: 'Girerd (Observance traitement)',
    category: 'Médecine interne',
    icon: Pill,
    description: 'Estimation niveau observance d\'un traitement',
    color: 'blue',
    fields: [
      { id: 'q1', label: 'Ce matin avez-vous oublié de prendre votre médicament?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'q2', label: 'Depuis la dernière consultation, avez-vous été en panne de médicament?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'q3', label: 'Vous est-il arrivé de prendre votre traitement avec retard?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'q4', label: 'Vous est-il arrivé de ne pas prendre votre traitement car vous aviez l\'impression qu\'il vous faisait plus de mal que de bien?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'q5', label: 'Vous est-il arrivé de ne pas prendre votre traitement parce que vous vous sentiez mieux?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]},
      { id: 'q6', label: 'Vous est-il arrivé de ne pas prendre votre traitement parce que vous vous sentiez moins bien?', type: 'select', options: [{ value: 0, label: 'Non' }, { value: 1, label: 'Oui' }]}
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += v; });
      return { score: total, max: 6 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: `Girerd ${score}/6`, detail: 'Bonne observance. Continuer ainsi.' };
      if (score <= 2) return { level: 'warning', text: `Girerd ${score}/6`, detail: 'Minime problème d\'observance. Renforcer l\'éducation thérapeutique.' };
      return { level: 'danger', text: `Girerd ${score}/6`, detail: 'Mauvaise observance. Identifier les obstacles et adapter le traitement.' };
    },
    formula: '0: bonne observance. 1-2: minimes pb. ≥3: mauvaise observance'
  },

  garden: {
    name: 'Garden (Fracture col fémur)',
    category: 'Orthopédie',
    icon: Bone,
    description: 'Classification fractures du col du fémur',
    color: 'orange',
    fields: [
      { id: 'stage', label: 'Stade Garden', type: 'select', options: [
        { value: 1, label: 'Stade I - Fracture incomplète, valgus' },
        { value: 2, label: 'Stade II - Fracture complète non déplacée' },
        { value: 3, label: 'Stade III - Fracture complète, déplacement partiel' },
        { value: 4, label: 'Stade IV - Fracture complète, déplacement total' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.stage || 1, max: 4 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Garden ${score}`, detail: 'Fracture non ou peu déplacée. Ostéosynthèse par vis. Risque nécrose ~10%.' };
      return { level: 'danger', text: `Garden ${score}`, detail: 'Fracture déplacée. Prothèse si > 60 ans. Risque nécrose ~30-40%.' };
    },
    formula: 'Garden I-II: stable. Garden III-IV: instable, risque nécrose tête fémorale'
  },

  // ==================== SCORES H-I ====================
  haq: {
    name: 'HAQ (Health Assessment Questionnaire)',
    category: 'Rhumatologie',
    icon: Bone,
    description: 'Évaluation incapacité polyarthrite rhumatoïde',
    color: 'orange',
    fields: [
      { id: 'dressing', label: 'S\'habiller', type: 'select', options: [
        { value: 0, label: '0 - Sans difficulté' },
        { value: 1, label: '1 - Avec difficulté' },
        { value: 2, label: '2 - Avec aide' },
        { value: 3, label: '3 - Impossible' }
      ]},
      { id: 'rising', label: 'Se lever d\'une chaise', type: 'select', options: [
        { value: 0, label: '0 - Sans difficulté' },
        { value: 1, label: '1 - Avec difficulté' },
        { value: 2, label: '2 - Avec aide' },
        { value: 3, label: '3 - Impossible' }
      ]},
      { id: 'eating', label: 'Manger', type: 'select', options: [
        { value: 0, label: '0 - Sans difficulté' },
        { value: 1, label: '1 - Avec difficulté' },
        { value: 2, label: '2 - Avec aide' },
        { value: 3, label: '3 - Impossible' }
      ]},
      { id: 'walking', label: 'Marcher', type: 'select', options: [
        { value: 0, label: '0 - Sans difficulté' },
        { value: 1, label: '1 - Avec difficulté' },
        { value: 2, label: '2 - Avec aide' },
        { value: 3, label: '3 - Impossible' }
      ]},
      { id: 'hygiene', label: 'Hygiène', type: 'select', options: [
        { value: 0, label: '0 - Sans difficulté' },
        { value: 1, label: '1 - Avec difficulté' },
        { value: 2, label: '2 - Avec aide' },
        { value: 3, label: '3 - Impossible' }
      ]},
      { id: 'reach', label: 'Atteindre objets', type: 'select', options: [
        { value: 0, label: '0 - Sans difficulté' },
        { value: 1, label: '1 - Avec difficulté' },
        { value: 2, label: '2 - Avec aide' },
        { value: 3, label: '3 - Impossible' }
      ]},
      { id: 'grip', label: 'Préhension', type: 'select', options: [
        { value: 0, label: '0 - Sans difficulté' },
        { value: 1, label: '1 - Avec difficulté' },
        { value: 2, label: '2 - Avec aide' },
        { value: 3, label: '3 - Impossible' }
      ]},
      { id: 'activities', label: 'Activités quotidiennes', type: 'select', options: [
        { value: 0, label: '0 - Sans difficulté' },
        { value: 1, label: '1 - Avec difficulté' },
        { value: 2, label: '2 - Avec aide' },
        { value: 3, label: '3 - Impossible' }
      ]}
    ],
    calculate: (values) => {
      let total = 0;
      let count = 0;
      Object.values(values).forEach(v => { if (v !== undefined) { total += v; count++; } });
      const avg = count > 0 ? (total / count) : 0;
      return { score: Math.round(avg * 100) / 100, max: 3 };
    },
    interpret: (score) => {
      if (score < 0.5) return { level: 'success', text: `HAQ ${score}`, detail: 'Pas ou peu d\'incapacité. Activité normale.' };
      if (score < 1.0) return { level: 'success', text: `HAQ ${score}`, detail: 'Incapacité légère. Quelques difficultés.' };
      if (score < 2.0) return { level: 'warning', text: `HAQ ${score}`, detail: 'Incapacité modérée. Aide partielle nécessaire.' };
      return { level: 'danger', text: `HAQ ${score}`, detail: 'Incapacité sévère. Aide quotidienne nécessaire.' };
    },
    formula: 'Moyenne des 8 domaines. 0: pas d\'incapacité. 3: incapacité maximale'
  },

  hoenhYahr: {
    name: 'Hoehn et Yahr (Parkinson)',
    category: 'Neurologie',
    icon: Brain,
    description: 'Stades évolution maladie de Parkinson',
    color: 'purple',
    fields: [
      { id: 'stage', label: 'Stade Hoehn et Yahr', type: 'select', options: [
        { value: 0, label: 'Stade 0 - Pas de signe' },
        { value: 1, label: 'Stade 1 - Atteinte unilatérale' },
        { value: 1.5, label: 'Stade 1.5 - Unilatéral + axial' },
        { value: 2, label: 'Stade 2 - Bilatéral sans trouble équilibre' },
        { value: 2.5, label: 'Stade 2.5 - Bilatéral léger, récupération au pull-test' },
        { value: 3, label: 'Stade 3 - Bilatéral, instabilité posturale, autonome' },
        { value: 4, label: 'Stade 4 - Handicap sévère mais peut marcher sans aide' },
        { value: 5, label: 'Stade 5 - Confiné fauteuil/lit sans aide' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.stage || 0, max: 5 };
    },
    interpret: (score) => {
      if (score <= 1.5) return { level: 'success', text: `Hoehn & Yahr ${score}`, detail: 'Stade précoce. Traitement symptomatique. Bonne qualité de vie.' };
      if (score <= 2.5) return { level: 'warning', text: `Hoehn & Yahr ${score}`, detail: 'Stade modéré. Ajustement traitement. Kinésithérapie.' };
      if (score <= 3) return { level: 'warning', text: `Hoehn & Yahr ${score}`, detail: 'Stade avancé. Risque de chutes. Rééducation posturale.' };
      return { level: 'danger', text: `Hoehn & Yahr ${score}`, detail: 'Stade sévère. Dépendance importante. Discussion stimulation cérébrale.' };
    },
    formula: 'Échelle de 0 à 5 évaluant la progression de la maladie de Parkinson'
  },

  homa: {
    name: 'HOMA-IR (Insulinorésistance)',
    category: 'Médecine interne',
    icon: Beaker,
    description: 'Évaluation de l\'insulinorésistance',
    color: 'orange',
    fields: [
      { id: 'glucose', label: 'Glycémie à jeun (mmol/L)', type: 'number', min: 2, max: 20, step: 0.1 },
      { id: 'insulin', label: 'Insulinémie à jeun (µUI/mL)', type: 'number', min: 1, max: 100, step: 0.1 }
    ],
    calculate: (values) => {
      const glucose = values.glucose || 5;
      const insulin = values.insulin || 10;
      const homa = (glucose * insulin) / 22.5;
      return { score: Math.round(homa * 100) / 100, max: 'variable' };
    },
    interpret: (score) => {
      if (score < 1.0) return { level: 'success', text: `HOMA-IR ${score}`, detail: 'Sensibilité à l\'insuline normale.' };
      if (score < 2.5) return { level: 'success', text: `HOMA-IR ${score}`, detail: 'Insulinosensibilité normale. Pas d\'insulinorésistance.' };
      if (score < 3.0) return { level: 'warning', text: `HOMA-IR ${score}`, detail: 'Insulinorésistance légère. Mesures hygiéno-diététiques.' };
      return { level: 'danger', text: `HOMA-IR ${score}`, detail: 'Insulinorésistance significative. Risque syndrome métabolique.' };
    },
    formula: 'HOMA-IR = (Glycémie × Insulinémie) / 22.5. Normal < 2.5'
  },

  // ==================== SCORES K-L ====================
  karnofsky: {
    name: 'Karnofsky (Performance status)',
    category: 'Oncologie',
    icon: Target,
    description: 'État de santé avant chimiothérapie',
    color: 'red',
    fields: [
      { id: 'status', label: 'Statut de performance', type: 'select', options: [
        { value: 100, label: '100% - Normal, pas de plainte' },
        { value: 90, label: '90% - Activité normale, symptômes mineurs' },
        { value: 80, label: '80% - Activité normale avec effort' },
        { value: 70, label: '70% - Autonome mais incapable de travailler' },
        { value: 60, label: '60% - Aide occasionnelle nécessaire' },
        { value: 50, label: '50% - Aide fréquente, soins médicaux fréquents' },
        { value: 40, label: '40% - Handicapé, soins spéciaux nécessaires' },
        { value: 30, label: '30% - Très handicapé, hospitalisation indiquée' },
        { value: 20, label: '20% - Très malade, traitement actif de support' },
        { value: 10, label: '10% - Moribond' },
        { value: 0, label: '0% - Décédé' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.status || 100, max: 100 };
    },
    interpret: (score) => {
      if (score >= 80) return { level: 'success', text: `Karnofsky ${score}%`, detail: 'Bon état général. Chimiothérapie curative possible.' };
      if (score >= 60) return { level: 'warning', text: `Karnofsky ${score}%`, detail: 'État modéré. Chimiothérapie adaptée possible.' };
      if (score >= 40) return { level: 'danger', text: `Karnofsky ${score}%`, detail: 'État altéré. Soins de support prioritaires.' };
      return { level: 'danger', text: `Karnofsky ${score}%`, detail: 'État très altéré. Soins palliatifs à discuter.' };
    },
    formula: 'Échelle de 0 à 100%. Seuil chimiothérapie généralement ≥ 60%'
  },

  killip: {
    name: 'Killip (Insuffisance cardiaque sur IDM)',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Gravité insuffisance cardiaque sur infarctus',
    color: 'red',
    fields: [
      { id: 'class', label: 'Classe Killip', type: 'select', options: [
        { value: 1, label: 'Classe I - Pas d\'insuffisance cardiaque' },
        { value: 2, label: 'Classe II - Crépitants < 50% champs, B3' },
        { value: 3, label: 'Classe III - OAP franc (crépitants > 50%)' },
        { value: 4, label: 'Classe IV - Choc cardiogénique' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.class || 1, max: 4 };
    },
    interpret: (score) => {
      if (score === 1) return { level: 'success', text: 'Killip I', detail: 'Pas d\'insuffisance cardiaque. Mortalité ~6%.' };
      if (score === 2) return { level: 'warning', text: 'Killip II', detail: 'IC modérée. Mortalité ~17%. Diurétiques, surveillance.' };
      if (score === 3) return { level: 'danger', text: 'Killip III', detail: 'OAP. Mortalité ~38%. Traitement urgent, USIC.' };
      return { level: 'danger', text: 'Killip IV', detail: 'Choc cardiogénique. Mortalité ~81%. Réanimation, assistance.' };
    },
    formula: 'Classification pronostique de l\'IDM selon la présence d\'IC'
  },

  lagrangeRigault: {
    name: 'Lagrange et Rigault (Fracture humérus enfant)',
    category: 'Orthopédie',
    icon: Bone,
    description: 'Classification fractures supra-condyliennes humérus',
    color: 'orange',
    fields: [
      { id: 'type', label: 'Type de fracture', type: 'select', options: [
        { value: 1, label: 'Type I - Fracture non déplacée' },
        { value: 2, label: 'Type II - Déplacement frontal isolé (varus/valgus)' },
        { value: 3, label: 'Type III - Déplacement sagittal (ant. ou post.)' },
        { value: 4, label: 'Type IV - Déplacement frontal + sagittal + rotatoire' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.type || 1, max: 4 };
    },
    interpret: (score) => {
      if (score === 1) return { level: 'success', text: 'Lagrange I', detail: 'Non déplacée. Traitement orthopédique (BABP 3 sem).' };
      if (score === 2) return { level: 'warning', text: 'Lagrange II', detail: 'Déplacement modéré. Réduction + BABP ou brochage.' };
      if (score === 3) return { level: 'warning', text: 'Lagrange III', detail: 'Déplacement sagittal. Réduction fermée + embrochage percutané.' };
      return { level: 'danger', text: 'Lagrange IV', detail: 'Déplacement complexe. Réduction au bloc + embrochage. Risque neurovasc.' };
    },
    formula: 'Classification pédiatrique des fractures supra-condyliennes'
  },

  lequesneGenou: {
    name: 'Lequesne Genou (Arthrose)',
    category: 'Orthopédie',
    icon: Bone,
    description: 'Évaluation handicap gonarthrose',
    color: 'orange',
    fields: [
      { id: 'pain_night', label: 'Douleur nocturne', type: 'select', options: [
        { value: 0, label: '0 - Non' },
        { value: 1, label: '1 - Seulement aux mouvements' },
        { value: 2, label: '2 - Même immobile' }
      ]},
      { id: 'pain_morning', label: 'Raideur matinale ou après repos assis', type: 'select', options: [
        { value: 0, label: '0 - Aucune ou < 1 min' },
        { value: 1, label: '1 - 1-15 min' },
        { value: 2, label: '2 - > 15 min' }
      ]},
      { id: 'pain_standing', label: 'Douleur debout 30 min', type: 'checkbox', points: 1 },
      { id: 'pain_walking', label: 'Douleur à la marche', type: 'checkbox', points: 1 },
      { id: 'pain_rising', label: 'Douleur pour se lever d\'un siège', type: 'checkbox', points: 1 },
      { id: 'distance', label: 'Distance de marche max', type: 'select', options: [
        { value: 0, label: '0 - Illimitée' },
        { value: 1, label: '1 - > 1 km mais limitée' },
        { value: 2, label: '2 - 500m - 1 km' },
        { value: 3, label: '3 - 300-500m' },
        { value: 4, label: '4 - 100-300m' },
        { value: 5, label: '5 - < 100m' },
        { value: 6, label: '6 - Avec canne' },
        { value: 7, label: '7 - Avec 2 cannes' }
      ]},
      { id: 'stairs', label: 'Difficulté escaliers', type: 'select', options: [
        { value: 0, label: '0 - Aucune difficulté' },
        { value: 1, label: '1 - Difficulté descente' },
        { value: 2, label: '2 - Difficulté montée et descente' }
      ]},
      { id: 'squat', label: 'Peut s\'accroupir', type: 'select', options: [
        { value: 0, label: '0 - Oui' },
        { value: 2, label: '2 - Non' }
      ]},
      { id: 'uneven', label: 'Marche terrain inégal', type: 'select', options: [
        { value: 0, label: '0 - Sans difficulté' },
        { value: 1, label: '1 - Avec difficulté' },
        { value: 2, label: '2 - Impossible' }
      ]}
    ],
    calculate: (values) => {
      let total = (values.pain_night || 0) + (values.pain_morning || 0) + (values.distance || 0) +
                  (values.stairs || 0) + (values.squat || 0) + (values.uneven || 0);
      if (values.pain_standing) total += 1;
      if (values.pain_walking) total += 1;
      if (values.pain_rising) total += 1;
      return { score: total, max: 24 };
    },
    interpret: (score) => {
      if (score <= 4) return { level: 'success', text: `Lequesne ${score}/24`, detail: 'Handicap léger. Traitement médical.' };
      if (score <= 7) return { level: 'warning', text: `Lequesne ${score}/24`, detail: 'Handicap modéré. Traitement médical + rééducation.' };
      if (score <= 10) return { level: 'warning', text: `Lequesne ${score}/24`, detail: 'Handicap important. Discussion chirurgicale possible.' };
      if (score <= 13) return { level: 'danger', text: `Lequesne ${score}/24`, detail: 'Handicap très important. Indication chirurgicale.' };
      return { level: 'danger', text: `Lequesne ${score}/24`, detail: 'Handicap extrême. Prothèse recommandée.' };
    },
    formula: '1-4: léger. 5-7: modéré. 8-10: important. 11-13: très important. ≥14: extrême'
  },

  lichtiger: {
    name: 'Lichtiger (Colite aiguë grave)',
    category: 'Hépato-Gastro',
    icon: Beaker,
    description: 'Suivi évolution colite aiguë grave',
    color: 'yellow',
    fields: [
      { id: 'stools', label: 'Nombre selles/jour', type: 'select', options: [
        { value: 0, label: '0 - 0-2' },
        { value: 1, label: '1 - 3-4' },
        { value: 2, label: '2 - 5-6' },
        { value: 3, label: '3 - 7-9' },
        { value: 4, label: '4 - ≥ 10' }
      ]},
      { id: 'night_stools', label: 'Selles nocturnes', type: 'select', options: [
        { value: 0, label: '0 - Non' },
        { value: 1, label: '1 - Oui' }
      ]},
      { id: 'blood', label: 'Sang visible dans selles', type: 'select', options: [
        { value: 0, label: '0 - Aucun' },
        { value: 1, label: '1 - Traces' },
        { value: 2, label: '2 - Modéré' },
        { value: 3, label: '3 - Abondant' }
      ]},
      { id: 'incontinence', label: 'Incontinence fécale', type: 'select', options: [
        { value: 0, label: '0 - Non' },
        { value: 1, label: '1 - Oui' }
      ]},
      { id: 'pain', label: 'Douleur abdominale', type: 'select', options: [
        { value: 0, label: '0 - Aucune' },
        { value: 1, label: '1 - Légère' },
        { value: 2, label: '2 - Modérée' },
        { value: 3, label: '3 - Sévère' }
      ]},
      { id: 'wellbeing', label: 'État général', type: 'select', options: [
        { value: 0, label: '0 - Parfait' },
        { value: 1, label: '1 - Bon' },
        { value: 2, label: '2 - Moyen' },
        { value: 3, label: '3 - Mauvais' },
        { value: 4, label: '4 - Très mauvais' }
      ]},
      { id: 'tenderness', label: 'Défense abdominale', type: 'select', options: [
        { value: 0, label: '0 - Aucune' },
        { value: 1, label: '1 - Légère' },
        { value: 2, label: '2 - Modérée' },
        { value: 3, label: '3 - Sévère' }
      ]},
      { id: 'steroids', label: 'Besoins en antidiarrhéiques', type: 'select', options: [
        { value: 0, label: '0 - Non' },
        { value: 1, label: '1 - Oui' }
      ]}
    ],
    calculate: (values) => {
      const total = (values.stools || 0) + (values.night_stools || 0) + (values.blood || 0) +
                    (values.incontinence || 0) + (values.pain || 0) + (values.wellbeing || 0) +
                    (values.tenderness || 0) + (values.steroids || 0);
      return { score: total, max: 21 };
    },
    interpret: (score) => {
      if (score <= 10) return { level: 'success', text: `Lichtiger ${score}/21`, detail: 'Réponse au traitement. Poursuite corticothérapie.' };
      return { level: 'danger', text: `Lichtiger ${score}/21`, detail: 'Échec corticothérapie. Discuter anti-TNF ou colectomie.' };
    },
    formula: 'Score > 10 après 3j IV = échec corticothérapie'
  }
};
